/**
 * StatsCard - User statistics overview
 *
 * Shows key metrics like reflection count, streak, and categories used.
 * Includes a storage usage bar when the user has a plan with a storage limit.
 */

import React from 'react';
import { formatFileSize } from '../../utils/formatFileSize';

function formatRecordingTime(seconds) {
  if (!seconds || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `~${h}h ${m}m`;
  if (m > 0) return `~${m}m`;
  return '<1m';
}

function StatsCard({ stats }) {
  const {
    totalReflections = 0,
    thisWeekReflections = 0,
    streak = 0,
    categoriesUsed = 0,
    storageUsed = 0,
    storageLimit = null,
    storageRemaining = null,
    planName = null,
    bytesPerSecondEstimate = null,
  } = stats || {};

  const approxSecondsRemaining = (bytesPerSecondEstimate && storageRemaining > 0)
    ? storageRemaining / bytesPerSecondEstimate
    : null;
  const timeRemaining = formatRecordingTime(approxSecondsRemaining);

  const hasStorage = storageLimit !== null && storageLimit > 0;
  const storageRatio = hasStorage ? storageUsed / storageLimit : 0;
  const storagePercent = Math.min(100, Math.round(storageRatio * 100));

  const getStorageColor = () => {
    if (storageRatio >= 1.0) return 'var(--color-danger, #ef4444)';
    if (storageRatio >= 0.9) return 'var(--color-warning, #f59e0b)';
    return 'var(--color-primary, #3b82f6)';
  };

  return (
    <div className="bento-card bento-card--medium">
      <div className="bento-card-header">
        <h2 className="bento-card-title">
          <span className="bento-card-icon">📊</span>
          Your Stats
        </h2>
      </div>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-value">{totalReflections}</span>
          <span className="stat-label">Total Reflections</span>
        </div>
        <div className="stat-item">
          <span className="stat-value streak">{streak}</span>
          <span className="stat-label">Day Streak</span>
        </div>
        <div className="stat-item">
          <span className="stat-value success">{thisWeekReflections}</span>
          <span className="stat-label">This Week</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{categoriesUsed}</span>
          <span className="stat-label">Categories</span>
        </div>
      </div>
      {hasStorage && (
        <div className="stat-storage">
          <div className="stat-storage-header">
            <span className="stat-storage-label">Storage</span>
            {planName && <span className="stat-storage-plan">{planName}</span>}
          </div>
          <div className="stat-storage-bar">
            <div
              className="stat-storage-fill"
              style={{
                width: `${storagePercent}%`,
                backgroundColor: getStorageColor()
              }}
            />
          </div>
          <div className="stat-storage-detail">
            {formatFileSize(storageUsed)} / {formatFileSize(storageLimit)}
            {timeRemaining && (
              <span className="stat-storage-time-remaining" title="Approximate recording time remaining based on your past recordings">
                {' '}· {timeRemaining} recording time remaining
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StatsCard;
